const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.vscapital.com.br";

export function getApiBase(): string {
  return API_BASE;
}

const ACCESS_KEY = "vscapital.access_token";
const REFRESH_KEY = "vscapital.refresh_token";
const USER_KEY = "vscapital.user";

export type AuthUser = {
  id: string;
  email: string;
  full_name: string;
  role: "admin" | "gestor_fundo" | "consultor" | "originador" | "cotista";
  is_admin: boolean;
  company_id?: string | null;
  company_type?: "gestora" | "originadora" | "vscapital" | null;
  company_name?: string | null;
};

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export function storeSession(payload: {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
}): void {
  localStorage.setItem(ACCESS_KEY, payload.access_token);
  localStorage.setItem(REFRESH_KEY, payload.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, public readonly details?: unknown) {
    super(code);
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  if (!res.ok) {
    clearSession();
    return null;
  }
  const body = (await res.json()) as { access_token?: string };
  if (!body.access_token) return null;
  localStorage.setItem(ACCESS_KEY, body.access_token);
  return body.access_token;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
  options: { retryOn401?: boolean } = { retryOn401: true },
): Promise<T> {
  const access = getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (access) headers.set("Authorization", `Bearer ${access}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && options.retryOn401 !== false) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      const retry = await fetch(`${API_BASE}${path}`, { ...init, headers });
      if (retry.ok) return (await retry.json()) as T;
      throw await asError(retry);
    }
  }

  if (!res.ok) throw await asError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function asError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  const code = (body as { error?: string })?.error ?? res.statusText ?? "request_failed";
  return new ApiError(res.status, code, body);
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw await asError(res);
  const body = (await res.json()) as { access_token: string; refresh_token: string; user: AuthUser };
  storeSession(body);
  return body.user;
}

export async function logout(): Promise<void> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  try {
    await apiFetch("/auth/logout", { method: "POST", body: JSON.stringify({ refresh_token: refresh }) }, { retryOn401: false });
  } catch {
    /* ignore */
  }
  clearSession();
}

export async function fetchMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>("/auth/me");
}
